/*
 * Copyright (c) 2024. Dave van Rijn Development
 */
import React, {KeyboardEventHandler, MouseEventHandler, PureComponent} from 'react';
import {DateType} from "../dateFieldController";
import {AwesomeIcon, ElementPosition, OrnamentShape, TextField, generateComponentId} from "../../../../../";
import {voidFunction} from "../../../util/controlUtil";
import {ErrorType} from "../../../util/interfaces";
import {DefaultInputProps} from "../../v2/inputController_v2";
import { IconName } from '@fortawesome/fontawesome-svg-core';
import IDate from '@dvrd/idate';

type DateIndex = {
    index: number;
    value: string;
    placeholder: string;
    mutable: boolean;
}

interface Props {
    onClickOrnament?: MouseEventHandler;
    onChange: (value: IDate) => void;
    onEnter?: KeyboardEventHandler;
    dateType: DateType;
    value: IDate | null;
    error?: ErrorType;
    label?: string | number;
    fullWidth?: boolean;
    disabled?: boolean;
}

interface State {
    indexes: DateIndex[];
}

const BACKSPACE = 'Backspace', ENTER = 'Enter', RIGHT = 'ArrowRight', LEFT = 'ArrowLeft', TAB = 'Tab';

const allowedKeys = new RegExp(`^\\d|${BACKSPACE}|${ENTER}|${RIGHT}|${LEFT}|${TAB}$`);

export default class DateInput extends PureComponent<Props, State> {
    static defaultProps = DefaultInputProps;
    private id = generateComponentId(null);

    constructor(props: Props) {
        super(props);
        this.state = {
            indexes: this.parseValue(props.value)
        };
    }

    onKeyDown = (evt: React.KeyboardEvent) => {
        const key = evt.key;
        if (allowedKeys.test(key)) {
            if (key !== TAB && key !== ENTER) {
                evt.persist();
                evt.preventDefault();
                if (key === BACKSPACE) this.onBackspace(evt);
                else if (key === RIGHT) this.setCaretPos(evt, 1);
                else if (key === LEFT) this.setCaretPos(evt, -1);
                else this.onEnterDigit(evt);
            }
        }
    };

    onBackspace = (evt: React.KeyboardEvent) => {
        const {start, end} = this.getCaretStartEnd(evt), indexes = this.getIndexesCopy();
        if (start > -1) {
            if (start < end) {
                // Remove selection
                this.onBackspaceSelection(start, end, evt);
            } else if (start > 0) {
                let prevPos = start - 1, nextCaret = prevPos;
                while (prevPos > 0 && !indexes[prevPos].mutable) prevPos--;
                while (nextCaret > 0 && !indexes[nextCaret - 1].mutable) nextCaret--;
                if (prevPos > -1) {
                    indexes[prevPos].value = indexes[prevPos].placeholder;
                    this.setState({indexes}, () => {
                        this.setCaretPos(evt, Math.max(0, nextCaret), false);
                    });
                }
            }
        }
    };

    onBackspaceSelection = (start: number, end: number, evt: React.KeyboardEvent) => {
        const indexes = this.getIndexesCopy();
        for (let i = start; i < end; i++)
            if (indexes[i].mutable) indexes[i].value = indexes[i].placeholder;
        this.setState({indexes}, () => {
            this.setCaretPos(evt, start, false);
        })
    }

    onEnterDigit = (evt: React.KeyboardEvent) => {
        const pos = this.getCaretPos(evt), indexes = this.getIndexesCopy();
        if (pos > -1 && pos < indexes.length) {
            indexes[pos].value = evt.key;
            let nextPos = pos + 1;
            while (nextPos < indexes.length && !indexes[nextPos].mutable) nextPos++;
            this.setState({indexes}, () => {
                this.setCaretPos(evt, nextPos, false);
            });
        }
    };

    getIndexesCopy = (): DateIndex[] => this.state.indexes.map((index: DateIndex) => ({
        value: index.value,
        mutable: index.mutable,
        placeholder: index.placeholder,
        index: index.index
    }));

    getCaretPos = (evt: React.KeyboardEvent): number => {
        const target: HTMLInputElement = evt.target as HTMLInputElement,
            pos = target.selectionStart;
        if (pos !== null) return pos;
        return -1;
    };

    getCaretStartEnd = (evt: React.KeyboardEvent): { start: number, end: number } => {
        const target = evt.target as HTMLInputElement;
        let start = target.selectionStart, end = target.selectionEnd;
        if (start === null) start = -1;
        if (end === null) end = -1;
        return {start, end};
    }

    setCaretPos = (evt: React.KeyboardEvent, position: number, relative: boolean = true) => {
        const target: HTMLInputElement = evt.target as HTMLInputElement;
        if (relative) {
            const newPosition = (target.selectionStart || 0) + position;
            target.selectionStart = newPosition;
            target.selectionEnd = newPosition;
        } else {
            target.selectionStart = position;
            target.selectionEnd = position;
        }
    };

    parseValue = (value: IDate | null): DateIndex[] => {
        const indexes: DateIndex[] = [], {dateType} = this.props;
        let chars: string, defaultChars: string;
        if (dateType === DateType.DATE) {
            defaultChars = 'dd-mm-jjjj';
            chars = value ? value.format('DD-MM-YYYY') : defaultChars;
        } else if (dateType === DateType.DATETIME) {
            defaultChars = 'dd-mm-jjjj uu:mm';
            chars = value ? value.format('DD-MM-YYYY HH:mm') : defaultChars;
        } else {
            defaultChars = 'uu:mm';
            chars = value ? value.format('HH:mm') : defaultChars;
        }
        const breakPoint: number = Math.min(chars.length, defaultChars.length);
        for (let i = 0; i < breakPoint; i++) {
            const char = chars.charAt(i);
            indexes.push({index: i, value: char, placeholder: defaultChars.charAt(i), mutable: !/[-: ]/.test(char)});
        }
        return indexes;
    };

    getStringValue = (length: number = -1): string => {
        const {indexes} = this.state;
        return (length > -1 ? indexes.slice(0, length) : indexes).reduce(
            (prev: string, current: DateIndex) => prev + current.value, '');
    };

    getIconName = (): IconName => this.props.dateType === DateType.TIME ? 'clock' : 'calendar-alt';

    tryOnChange = (indexes: DateIndex[]) => {
        const {dateType, onChange} = this.props;
        let momentValue: IDate,
            format: string = dateType === DateType.DATE ? 'DD-MM-YYYY' : dateType === DateType.DATETIME ?
                'DD-MM-YYYY HH:mm' : 'HH:mm';
        if (indexes.length >= format.length) {
            const stringValue = this.getStringValue(format.length);
            momentValue = new IDate(stringValue, format);
            if (!/[a-zA-Z]/.test(stringValue) && momentValue.isValid()) onChange(momentValue);
        }
    };

    indexesAreDifferent = (indexes: DateIndex[], prevIndexes: DateIndex[]) => {
        if (indexes.length !== prevIndexes.length) return true;
        for (let i = 0; i < indexes.length; i++)
            if (indexes[i].value !== prevIndexes[i].value) return true;
        return false;
    };

    renderOrnament = (): OrnamentShape | [] => {
        const {onClickOrnament} = this.props;
        if (onClickOrnament) return {
            placement: ElementPosition.RIGHT,
            element: <AwesomeIcon name={this.getIconName()} onClick={onClickOrnament}/>,
            allowPropagation: false,
        };
        return [];
    };

    componentDidUpdate = (prevProps: Props, prevState: State) => {
        const {indexes} = this.state, prevIndexes = prevState.indexes, {value} = this.props,
            prevValue = prevProps.value;
        if (this.indexesAreDifferent(indexes, prevIndexes)) this.tryOnChange(indexes);
        if ((!prevValue && value) || (!value && prevValue) || (value && prevValue && !value.isAt(prevValue)))
            this.setState({indexes: this.parseValue(value)});
    };

    render = () => {
        const {label, error, fullWidth, onEnter, disabled} = this.props;
        return (
            <TextField onChange={voidFunction} value={this.getStringValue()} inputProps={{onKeyDown: this.onKeyDown}}
                       ornaments={this.renderOrnament()} id={this.id} label={label} error={error}
                       fullWidth={fullWidth} onEnter={onEnter} disabled={disabled}/>
        );
    };
}